import { useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import type { Participant, ParticipantDetails } from '@regigov/contracts';
import { ParticipantFields, toFormValues, validateDetails, type FieldErrors } from './ParticipantForm';
import { DefinitionList, ProblemAlert, StatusBadge, formatDateTime } from './ui';

export function ParticipantSummary({ participant: p }: { participant: Participant }) {
  return (
    <div className="cards">
      <section className="card">
        <h2>Organisation</h2>
        <DefinitionList
          items={[
            ['Participant ID', <code key="id">{p.id}</code>],
            ['Status', <StatusBadge key="s" status={p.status} />],
            ['Legal name', p.legalName],
            ['Trading name', p.tradingName],
            ['ABN', p.abn],
            ['Type', p.participantType],
          ]}
        />
      </section>
      <section className="card">
        <h2>Contact & address</h2>
        <DefinitionList
          items={[
            ['Contact', p.contactName],
            ['Email', p.contactEmail],
            ['Phone', p.contactPhone],
            ['Address', `${p.address.line1}${p.address.line2 ? `, ${p.address.line2}` : ''}, ${p.address.suburb} ${p.address.state} ${p.address.postcode}`],
            ['Record version', p.version],
            ['Last updated', formatDateTime(p.updatedAt)],
          ]}
        />
      </section>
    </div>
  );
}

interface EditorProps {
  participant: Participant;
  /** CER can change identity fields and status; participants only contact/address. */
  mode: 'cer' | 'self';
  onSave: (patch: {
    details: Partial<ParticipantDetails>;
    status?: 'approved' | 'suspended';
    changeReason: string;
    validFrom?: string;
    correctionOf?: number;
  }) => Promise<Participant>;
  onSaved: (p: Participant) => void;
}

const SELF_FIELDS: (keyof ParticipantDetails)[] = ['tradingName', 'contactName', 'contactEmail', 'contactPhone', 'address'];

export function ParticipantEditor({ participant, mode, onSave, onSaved }: EditorProps) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(() => toFormValues(participant));
  const [status, setStatus] = useState<'approved' | 'suspended' | ''>('');
  const [reason, setReason] = useState('');
  const [correctionOf, setCorrectionOf] = useState('');
  const [validFrom, setValidFrom] = useState('');
  const [errors, setErrors] = useState<FieldErrors>({});

  const save = useMutation({
    mutationFn: async () => {
      const { details, errors: fieldErrors } = validateDetails(values);
      setErrors(fieldErrors);
      if (!details) throw new Error('Fix the highlighted fields');
      const patch: Partial<ParticipantDetails> = {};
      for (const key of Object.keys(details) as (keyof ParticipantDetails)[]) {
        if (mode === 'self' && !SELF_FIELDS.includes(key)) continue;
        if (JSON.stringify(details[key]) !== JSON.stringify(participant[key])) Object.assign(patch, { [key]: details[key] });
      }
      return onSave({
        details: patch,
        ...(status && status !== participant.status && { status }),
        changeReason: reason,
        ...(mode === 'cer' && correctionOf && validFrom && {
          correctionOf: Number(correctionOf),
          validFrom: new Date(validFrom).toISOString(),
        }),
      });
    },
    onSuccess: (p) => {
      setOpen(false);
      setReason('');
      setStatus('');
      setCorrectionOf('');
      setValidFrom('');
      setValues(toFormValues(p));
      onSaved(p);
    },
  });

  const canSuspend = mode === 'cer' && (participant.status === 'approved' || participant.status === 'suspended');

  if (!open) {
    return (
      <div>
        <button type="button" onClick={() => setOpen(true)}>
          {mode === 'cer' ? 'Correct record / change status' : 'Update organisation details'}
        </button>
      </div>
    );
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    save.mutate();
  }

  return (
    <form className="form card" onSubmit={onSubmit} noValidate>
      <h2>{mode === 'cer' ? 'Correct participant record' : 'Update organisation details'}</h2>
      <ParticipantFields values={values} onChange={setValues} errors={errors} identityEditable={mode === 'cer'} disabled={save.isPending} />
      {canSuspend && (
        <label>
          Status
          <select name="status" value={status || participant.status} onChange={(e) => setStatus(e.target.value as 'approved' | 'suspended')}>
            <option value="approved">approved</option>
            <option value="suspended">suspended</option>
          </select>
        </label>
      )}
      {mode === 'cer' && (
        <fieldset>
          <legend>Correction lineage (optional)</legend>
          <div className="grid2">
            <label>
              Superseded version ID (the #id shown in History)
              <input
                name="correctionOf"
                type="number"
                min="1"
                value={correctionOf}
                onChange={(e) => setCorrectionOf(e.target.value)}
                required={Boolean(validFrom)}
              />
            </label>
            <label>
              Correct facts valid from
              <input
                name="validFrom"
                type="datetime-local"
                value={validFrom}
                onChange={(e) => setValidFrom(e.target.value)}
                required={Boolean(correctionOf)}
              />
            </label>
          </div>
        </fieldset>
      )}
      <label>
        Reason for change (recorded against this version)
        <textarea name="changeReason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} required minLength={3} maxLength={1000} />
      </label>
      <ProblemAlert error={save.error} />
      <div className="row">
        <button
          type="submit"
          disabled={save.isPending || reason.trim().length < 3 || Boolean(correctionOf) !== Boolean(validFrom)}
        >
          Save new version
        </button>
        <button type="button" className="secondary" onClick={() => setOpen(false)} disabled={save.isPending}>
          Cancel
        </button>
      </div>
    </form>
  );
}
